import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImageUploader from "@/components/ImageUploader";
import { Plus, Trash, PencilSimple, Eye, ChartLineUp, X, Megaphone, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const SLOTS = [
    { value: "hero", label: "Homepage Hero (Anchor)" },
    { value: "feed", label: "In-feed Card (Trade Partner)" },
    { value: "discover_top", label: "Discover Top Banner (Spotlight)" },
    { value: "profile_inline", label: "Builder Profile Inline" },
    { value: "sidebar", label: "Sidebar (Dashboard)" },
    { value: "footer", label: "Footer" },
];

const empty = {
    title: "",
    advertiser_name: "",
    description: "",
    image_path: null,
    image_url: "",
    link_url: "",
    slot: "feed",
    is_active: true,
    cta_label: "Visit advertiser",
};

export default function Admin() {
    const { user, loading } = useAuth();
    const [tab, setTab] = useState("ads");
    const [ads, setAds] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const [a, q] = await Promise.all([api.get("/admin/ads"), api.get("/admin/inquiries")]);
            setAds(a.data);
            setInquiries(q.data);
        } catch (e) {
            toast.error("Could not load admin data");
        }
    };

    useEffect(() => {
        if (user?.role === "admin") load();
    }, [user]);

    if (loading) return <div className="max-w-5xl mx-auto px-6 py-16 text-overline text-neutral-500">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "admin") {
        return (
            <div className="max-w-3xl mx-auto px-6 py-24 text-center">
                <h1 className="font-display text-4xl font-black uppercase mb-3">Admin only</h1>
                <p className="text-neutral-600 mb-6">This area is for platform administrators.</p>
                <Link to="/dashboard" className="btn-ghost inline-flex">Back to dashboard</Link>
            </div>
        );
    }

    const startCreate = () => {
        setEditing(null);
        setForm(empty);
        setShowForm(true);
    };

    const startEdit = (ad) => {
        setEditing(ad.id);
        setForm({
            title: ad.title || "",
            advertiser_name: ad.advertiser_name || "",
            description: ad.description || "",
            image_path: ad.image_path || null,
            image_url: ad.image_url || "",
            link_url: ad.link_url || "",
            slot: ad.slot || "feed",
            is_active: ad.is_active !== false,
            cta_label: ad.cta_label || "Visit advertiser",
        });
        setShowForm(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.link_url.trim()) {
            return toast.error("Title and link URL are required");
        }
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/admin/ads/${editing}`, form);
                toast.success("Ad updated");
            } else {
                await api.post("/admin/ads", form);
                toast.success("Ad created");
            }
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const removeAd = async (id) => {
        if (!window.confirm("Delete this ad?")) return;
        await api.delete(`/admin/ads/${id}`);
        toast.success("Ad deleted");
        load();
    };

    const updateInquiry = async (id, status) => {
        await api.put(`/admin/inquiries/${id}?status=${status}`);
        load();
    };

    const totalImpressions = ads.reduce((s, a) => s + (a.impressions || 0), 0);
    const totalClicks = ads.reduce((s, a) => s + (a.clicks || 0), 0);
    const newInquiries = inquiries.filter((i) => i.status === "new").length;

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
            <div className="text-overline text-[#E63946] mb-2">Admin</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-none uppercase mb-8">
                Monetisation hub.
            </h1>

            {/* Stats */}
            <div className="grid sm:grid-cols-4 border border-black bg-white mb-8">
                {[
                    { label: "Active ads", value: ads.filter((a) => a.is_active).length },
                    { label: "Impressions", value: totalImpressions },
                    { label: "Clicks", value: totalClicks },
                    { label: "New inquiries", value: newInquiries, accent: newInquiries > 0 },
                ].map((s, i) => (
                    <div key={s.label} className={`p-5 ${i < 3 ? "sm:border-r border-black border-b sm:border-b-0" : ""}`}>
                        <div className="text-overline text-neutral-500 mb-2">{s.label}</div>
                        <div className={`font-display text-4xl font-black leading-none ${s.accent ? "text-[#E63946]" : ""}`}>
                            {s.value}
                        </div>
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-0 border-b border-black mb-6">
                {[
                    { id: "ads", label: "Ad placements", icon: Megaphone },
                    { id: "inquiries", label: `Inquiries${newInquiries > 0 ? ` (${newInquiries})` : ""}`, icon: EnvelopeSimple },
                ].map((t) => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            data-testid={`admin-tab-${t.id}`}
                            className={`px-5 py-3 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider border-b-2 -mb-[1px] ${
                                tab === t.id ? "border-[#E63946] text-black" : "border-transparent text-neutral-500 hover:text-black"
                            }`}
                        >
                            <Icon size={16} weight="duotone" /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "ads" && (
                <>
                    <div className="flex justify-between items-center mb-6">
                        <p className="text-sm text-neutral-600">Manage ad placements across the site.</p>
                        <button onClick={startCreate} className="btn-accent" data-testid="new-ad-btn">
                            <Plus size={16} weight="bold" /> New ad
                        </button>
                    </div>

                    {ads.length === 0 ? (
                        <div className="border border-dashed border-neutral-400 p-12 text-center">
                            <Megaphone size={36} weight="duotone" className="mx-auto mb-3" />
                            <p className="text-neutral-600">No ads yet. Create your first placement.</p>
                        </div>
                    ) : (
                        <div className="border border-black bg-white">
                            <table className="w-full text-sm">
                                <thead className="bg-black text-white">
                                    <tr>
                                        <th className="text-left p-3 text-overline font-bold">Ad</th>
                                        <th className="text-left p-3 text-overline font-bold hidden md:table-cell">Slot</th>
                                        <th className="text-left p-3 text-overline font-bold hidden md:table-cell">Status</th>
                                        <th className="text-left p-3 text-overline font-bold hidden lg:table-cell"><ChartLineUp size={14} className="inline" /> Imp / Clk</th>
                                        <th className="text-right p-3 text-overline font-bold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ads.map((ad) => (
                                        <tr key={ad.id} className="border-t border-neutral-200" data-testid={`ad-row-${ad.id}`}>
                                            <td className="p-3">
                                                <div className="flex items-center gap-3">
                                                    {(ad.image_url || ad.image_path) && (
                                                        <img
                                                            src={ad.image_url || fileUrl(ad.image_path)}
                                                            alt=""
                                                            className="w-12 h-12 object-cover border border-neutral-200 flex-shrink-0"
                                                        />
                                                    )}
                                                    <div className="min-w-0">
                                                        <div className="font-bold truncate">{ad.title}</div>
                                                        <div className="text-xs text-neutral-500 truncate">{ad.advertiser_name || "—"}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-3 hidden md:table-cell">
                                                <span className="tag-chip">{ad.slot}</span>
                                            </td>
                                            <td className="p-3 hidden md:table-cell">
                                                {ad.is_active ? (
                                                    <span className="text-xs uppercase tracking-widest text-[#E63946] font-bold">Live</span>
                                                ) : (
                                                    <span className="text-xs uppercase tracking-widest text-neutral-400 font-bold">Off</span>
                                                )}
                                            </td>
                                            <td className="p-3 hidden lg:table-cell text-xs font-mono-accent">
                                                {ad.impressions || 0} / {ad.clicks || 0}
                                            </td>
                                            <td className="p-3 text-right">
                                                <div className="inline-flex gap-2">
                                                    <button onClick={() => startEdit(ad)} data-testid={`edit-ad-${ad.id}`} className="border border-black p-2 hover:bg-black hover:text-white">
                                                        <PencilSimple size={14} weight="bold" />
                                                    </button>
                                                    <button onClick={() => removeAd(ad.id)} data-testid={`delete-ad-${ad.id}`} className="border border-black p-2 hover:bg-[#E63946] hover:text-white hover:border-[#E63946]">
                                                        <Trash size={14} weight="bold" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {tab === "inquiries" && (
                <>
                    {inquiries.length === 0 ? (
                        <div className="border border-dashed border-neutral-400 p-12 text-center">
                            <EnvelopeSimple size={36} weight="duotone" className="mx-auto mb-3" />
                            <p className="text-neutral-600">No advertiser inquiries yet.</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {inquiries.map((q) => (
                                <div key={q.id} className="border border-black bg-white p-5" data-testid={`inquiry-${q.id}`}>
                                    <div className="flex items-start justify-between gap-4 mb-3">
                                        <div>
                                            <div className="font-display text-xl font-bold leading-tight">{q.company_name}</div>
                                            <div className="text-xs text-neutral-500 uppercase tracking-widest mt-1">
                                                {q.contact_name} · {q.email}
                                                {q.phone ? ` · ${q.phone}` : ""}
                                            </div>
                                        </div>
                                        <select
                                            value={q.status}
                                            onChange={(e) => updateInquiry(q.id, e.target.value)}
                                            className="input-field !w-auto !py-1 !px-2 text-xs uppercase font-bold"
                                            data-testid={`inquiry-status-${q.id}`}
                                        >
                                            <option value="new">New</option>
                                            <option value="contacted">Contacted</option>
                                            <option value="closed_won">Won</option>
                                            <option value="closed_lost">Lost</option>
                                        </select>
                                    </div>
                                    <div className="grid sm:grid-cols-3 gap-4 text-sm border-t border-neutral-200 pt-3">
                                        <div>
                                            <div className="text-overline text-neutral-500 mb-1">Package</div>
                                            <div>{q.package || "—"}</div>
                                        </div>
                                        <div>
                                            <div className="text-overline text-neutral-500 mb-1">Budget</div>
                                            <div>{q.budget || "—"}</div>
                                        </div>
                                        <div>
                                            <div className="text-overline text-neutral-500 mb-1">Received</div>
                                            <div>{new Date(q.created_at).toLocaleString("en-GB")}</div>
                                        </div>
                                    </div>
                                    {q.message && (
                                        <p className="text-sm text-neutral-700 mt-3 whitespace-pre-line border-l-2 border-[#E63946] pl-3">
                                            {q.message}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {/* Ad form modal */}
            {showForm && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
                    <form
                        onSubmit={submit}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 sm:p-8 fade-in-up"
                        data-testid="ad-form"
                    >
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <div className="text-overline text-[#E63946] mb-1">{editing ? "Edit ad" : "New ad"}</div>
                                <h2 className="font-display text-2xl font-black uppercase">Placement details</h2>
                            </div>
                            <button type="button" onClick={() => setShowForm(false)} className="p-2 hover:bg-neutral-100">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-overline text-neutral-700 block mb-2">Title *</label>
                                    <input
                                        required
                                        value={form.title}
                                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                                        className="input-field"
                                        data-testid="ad-title-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-overline text-neutral-700 block mb-2">Advertiser name</label>
                                    <input
                                        value={form.advertiser_name}
                                        onChange={(e) => setForm({ ...form, advertiser_name: e.target.value })}
                                        className="input-field"
                                        data-testid="ad-advertiser-input"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-overline text-neutral-700 block mb-2">Description</label>
                                <textarea
                                    rows={2}
                                    value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    className="input-field resize-none"
                                    data-testid="ad-description-input"
                                />
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-overline text-neutral-700 block mb-2">Slot</label>
                                    <select
                                        value={form.slot}
                                        onChange={(e) => setForm({ ...form, slot: e.target.value })}
                                        className="input-field"
                                        data-testid="ad-slot-input"
                                    >
                                        {SLOTS.map((s) => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-overline text-neutral-700 block mb-2">Link URL *</label>
                                    <input
                                        required
                                        type="url"
                                        value={form.link_url}
                                        onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                                        className="input-field"
                                        placeholder="https://..."
                                        data-testid="ad-link-input"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="text-overline text-neutral-700 block mb-2">Image URL</label>
                                <input
                                    value={form.image_url || ""}
                                    onChange={(e) => setForm({ ...form, image_url: e.target.value, image_path: null })}
                                    className="input-field"
                                    placeholder="https://images.example.com/banner.jpg (or upload below)"
                                    data-testid="ad-image-url-input"
                                />
                                <p className="text-xs text-neutral-500 mt-2">Or upload an image:</p>
                                <div className="mt-2">
                                    <ImageUploader
                                        images={form.image_path ? [form.image_path] : []}
                                        setImages={(arr) => setForm({ ...form, image_path: arr[0] || null, image_url: arr[0] ? "" : form.image_url })}
                                        max={1}
                                    />
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-overline text-neutral-700 block mb-2">CTA label</label>
                                    <input
                                        value={form.cta_label}
                                        onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                                        className="input-field"
                                        data-testid="ad-cta-input"
                                    />
                                </div>
                                <div className="flex items-end">
                                    <label className="inline-flex items-center gap-2 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={form.is_active}
                                            onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                                            data-testid="ad-active-input"
                                        />
                                        Live (active)
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex gap-3">
                            <button type="submit" disabled={saving} className="btn-accent" data-testid="ad-save-btn">
                                {saving ? "Saving…" : editing ? "Save changes" : "Create ad"}
                            </button>
                            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">Cancel</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
